import React from 'react';
import OrderDesign from './OrderDesign';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';

// Faction colors for completed orders display
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

const combinedFactions = {
  'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
  'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
  'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
  'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
  'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
  'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
  'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
  'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
  'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
  'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
};

function CompletedOrdersModal({ show, onClose, completedOrders, gameState, userId = null, hoveredOrder, onHoverOrder, essiaLuhrActive = false, onEssiaLuhrSelect = null }) {
  const { t } = useTranslation();

  if (!show || !completedOrders) return null;

  const hoveredOrderData = hoveredOrder !== null && completedOrders.length > 0
    ? completedOrders.find((o, idx) => (o.instanceId || o.id || idx) === hoveredOrder)
    : null;

  return (
    <div 
      className="modal-overlay"
      style={{
        position: 'absolute',
        top: '-100%',
        left: '-100%',
        width: '300%',
        height: '300%',
        background: 'rgba(0,0,0,0.7)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Expanded OrderDesign shown to the right of the modal */}
      {hoveredOrderData && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 3000,
            pointerEvents: 'none'
          }}
        >
          <OrderDesign
            order={hoveredOrderData}
            count={0}
            minimal={false}
            lazyLoad={true}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div 
        style={{
          background: '#121212',
          padding: '24px',
          borderRadius: '12px',
          minWidth: '400px',
          maxWidth: '600px',
          maxHeight: '864px', // 80% of 1080px base height
          overflowY: 'auto',
          overflowX: 'visible',
          boxShadow: '0 0 24px #000',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #333',
          paddingBottom: '10px'
        }}>
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.completedOrders') || 'Completed Orders'}
          </h3>
          <button
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              onClose();
            }}
            style={{
              background: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '1em',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#444'}
            onMouseLeave={(e) => e.target.style.background = '#333'}
          >
            {t('battleUI.close') || 'Close'}
          </button>
        </div>
        
        <div style={{ marginBottom: '10px', color: '#aaa', fontSize: '0.9em' }}>
          {t('battleUI.totalCompletedOrders') || 'Total'}: {completedOrders.length || 0}
        </div>

        <div style={{ 
          maxHeight: '648px', // 60% of 1080px base height
          overflowY: 'auto',
          scrollbarColor: '#333 #121212',
          scrollbarWidth: 'thin',
          position: 'relative'
        }}>
          {completedOrders && completedOrders.length > 0 ? (
            completedOrders
              .filter(order => !essiaLuhrActive || order.type === 'Strategem')
              .map((order, index) => {
                const color = order
                  ? (combinedFactions[order.faction_name]
                      ? factionColors['Combined']
                      : factionColors[order.faction_name] || '#FFFFFF')
                  : '#FFFFFF';
                
                const isHovered = hoveredOrder !== null && 
                  (order.instanceId === hoveredOrder || order.id === hoveredOrder || index === hoveredOrder);
                const isStrategem = order.type === 'Strategem';
                const showGetButton = essiaLuhrActive && isStrategem && isHovered && onEssiaLuhrSelect;

                return (
                  <div
                    key={order.instanceId || order.id || index}
                    onMouseEnter={() => {
                      playSoundEffect('/sounds/hoverover.mp3', 0.3);
                      onHoverOrder(order.instanceId || order.id || index);
                    }}
                    onMouseLeave={() => onHoverOrder(null)}
                    style={{
                      border: essiaLuhrActive && isStrategem
                        ? '2px solid rgba(255, 215, 0, 0.8)' // Yellow border when Essia Luhr is active
                        : '0px solid #ccc',
                      padding: '5px',
                      marginBottom: '5px',
                      borderRadius: '4px',
                      backgroundColor: essiaLuhrActive && isStrategem
                        ? 'rgba(255, 215, 0, 0.3)' // Yellow background when Essia Luhr is active
                        : color,
                      fontSize: '0.9em',
                      cursor: 'default',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong style={{
                        color: essiaLuhrActive && isStrategem
                          ? '#FFD700' // Yellow text when Essia Luhr is active
                          : 'inherit'
                      }}>
                        {order.name || 'Unknown Order'}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{order.cost ?? 'N/A'}</span>
                      {showGetButton && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEssiaLuhrSelect(order.instanceId || order.id, order.id);
                          }}
                          style={{
                            background: '#FFD700',
                            color: '#000',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#FFA500';
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#FFD700';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          {t('battleUI.get') || 'Get'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
          ) : (
            <div style={{ color: '#aaa', textAlign: 'center', padding: '20px' }}>
              {essiaLuhrActive
                ? (t('battleUI.noStrategemsInCompletedOrders') || 'No strategems in completed orders')
                : (t('battleUI.noCompletedOrders') || 'No completed orders yet')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompletedOrdersModal;

